import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Card,
  FileButton,
  Group,
  Modal,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { useState } from "react";
import { formatNumber } from "utiles/url";

// GraphQL Mutation Definition
const RECORD_BANK_REPAYMENT = gql`
  mutation RecordBankRepayment(
    $loan_id: ID!
    $amount: Float!
    $bank_name: String!
    $reference_number: String!
    $payment_date: String!
    $slip: Upload
  ) {
    recordBankRepayment(
      loan_id: $loan_id
      amount: $amount
      bank_name: $bank_name
      reference_number: $reference_number
      payment_date: $payment_date
      slip: $slip
    ) {
      id
      amount
      payment_method
      bank_slip_url
    }
  }
`;

const useStyles = createStyles((theme) => ({
  header: {
    position: "sticky",
    top: 0,
    backgroundColor: theme.colors.gray[0],
    zIndex: 1,
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      borderBottom: `1px solid ${theme.colors.gray[3]}`,
    },
  },
  unpaidRow: {
    backgroundColor: "#fff5f5 !important",
    "&:hover": { backgroundColor: "#ffe3e3 !important" },
  },
  warningRow: {
    backgroundColor: "#fff9db !important",
    "&:hover": { backgroundColor: "#fff3bf !important" },
  },
  tableHeaderText: {
    fontSize: "11px",
    textTransform: "capitalize",
    fontWeight: 700,
    color: theme.colors.gray[7],
  },
}));

const LoanRequestReportDetail = ({ requestData, setOpenedDetal, refetch }) => {
  const { classes, cx } = useStyles();

  // --- Modal & Form State ---
  const [opened, setOpened] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [formData, setFormData] = useState({
    amount: 0,
    bank_name: "",
    reference_number: "",
    slip: null,
  });
  const [recordRepayment, { loading }] = useMutation(RECORD_BANK_REPAYMENT, {
    onCompleted: () => {
      showNotification({
        color: "green",
        title: "Success",
        message: "Repayment recorded successfully",
      });
      setOpened(false);
      setOpenedDetal(false);
      resetForm();
      refetch();
    },
    onError: (error) => {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to record repayment",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      amount: 0,
      bank_name: "",
      reference_number: "",
      slip: null,
    });
    setSelectedLoan(null);
  };

  const handleOpenPayment = (row) => {
    setSelectedLoan(row);
    setFormData((prev) => ({
      ...prev,
      amount: row.remaining_balance,
    }));
    setOpened(true);
  };

  const handleSubmit = () => {
    if (!selectedLoan) return;

    recordRepayment({
      variables: {
        loan_id: selectedLoan.id,
        amount: formData.amount,
        bank_name: formData.bank_name,
        reference_number: formData.reference_number,
        payment_date: new Date().toISOString(),
        slip: formData.slip,
      },
    });
  };

  const rows = requestData?.map((row, index) => {
    const hasRemainingBalance = row?.remaining_balance > 0;
    const dueDate = new Date(row?.due_date);
    const today = new Date();
    const diffInTime = dueDate.getTime() - today.getTime();
    const diffInDays = diffInTime / (1000 * 3600 * 24);

    const isWarning = diffInDays > 15 && hasRemainingBalance;
    const isUnpaid = hasRemainingBalance;

    return (
      <tr
        key={row.id || index}
        className={cx({
          [classes.unpaidRow]: isUnpaid && !isWarning,
          [classes.warningRow]: isWarning,
        })}
      >
        <td>
          <Text size="xs" weight={500} style={{ lineHeight: 1.2 }}>
            {row.microfinance?.name || "Not Assigned"}
          </Text>
          {isWarning && (
            <Text
              weight={700}
              color="orange.9"
              sx={{
                fontSize: "9px",
                lineHeight: 1,
                marginTop: "2px",
                textTransform: "uppercase",
              }}
            >
              Critical Overdue (15+ DAYS)
            </Text>
          )}
        </td>
        <td>
          <Text size="sm">{formatNumber(row?.total_payable)}</Text>
        </td>
        <td>
          <Text size="sm">{formatNumber(row?.paid_amount)}</Text>
        </td>
        <td>
          <Text
            size="sm"
            weight={hasRemainingBalance ? 700 : 400}
            color={isUnpaid ? "red.7" : "black"}
          >
            {formatNumber(row?.remaining_balance)}
          </Text>
        </td>
        <td>
          <Text
            size="sm"
            color={row.status === "PAID" ? "green" : "orange"}
            weight={500}
          >
            {row.status}
          </Text>
        </td>
        <td>
          <Text size="sm">{row.due_date?.split(" ")[0]}</Text>
        </td>
        <td>
          {hasRemainingBalance && (
            <Button
              size="xs"
              variant="filled"
              color="blue"
              onClick={() => handleOpenPayment(row)}
            >
              Pay
            </Button>
          )}
        </td>
      </tr>
    );
  });

  return (
    <div style={{ width: "98%", margin: "auto", marginTop: "20px" }}>
      <Card shadow="sm" p="lg" withBorder>
        <ScrollArea sx={{ height: 500 }}>
          <Table horizontalSpacing="md" verticalSpacing="sm" highlightOnHover>
            <thead className={classes.header}>
              <tr>
                <th className={classes.tableHeaderText}>Microfinance</th>
                <th className={classes.tableHeaderText}>Total Amount</th>
                <th className={classes.tableHeaderText}>Paid Amount</th>
                <th className={classes.tableHeaderText}>Remaining Amount</th>
                <th className={classes.tableHeaderText}>Status</th>
                <th className={classes.tableHeaderText}>End date</th>
                <th className={classes.tableHeaderText}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows && rows.length > 0 ? (
                rows
              ) : (
                <tr>
                  <td colSpan={7}>
                    <Text align="center" color="dimmed" py="xl">
                      No loan data available
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
      </Card>

      {/* --- Repayment Modal --- */}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={<Text weight={700}>Record Bank Repayment</Text>}
        centered
      >
        <Stack spacing="md">
          <NumberInput
            label="Repayment Amount"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(val) => setFormData({ ...formData, amount: val })}
            required
            precision={2}
            min={0}
          />

          <TextInput
            label="Bank Name"
            placeholder="e.g. Commercial Bank of Ethiopia"
            value={formData.bank_name}
            onChange={(e) =>
              setFormData({ ...formData, bank_name: e.target.value })
            }
            required
          />

          <TextInput
            label="Reference Number"
            placeholder="TXN..."
            value={formData.reference_number}
            onChange={(e) =>
              setFormData({ ...formData, reference_number: e.target.value })
            }
            required
          />

          {/* Slip Upload */}
          <Stack spacing={5}>
            <Text size="sm" weight={500}>
              Payment Slip (Image or PDF){" "}
              <span style={{ color: "red" }}>*</span>
            </Text>
            <Group>
              <FileButton
                onChange={(file) => setFormData({ ...formData, slip: file })}
                accept="image/png,image/jpeg,application/pdf"
              >
                {(props) => (
                  <Button {...props} variant="outline" color="blue">
                    {formData.slip ? "Change Slip" : "Upload Slip"}
                  </Button>
                )}
              </FileButton>
              {formData.slip && (
                <Text size="xs" color="dimmed" sx={{ maxWidth: 150 }} truncate>
                  {formData.slip.name}
                </Text>
              )}
            </Group>
          </Stack>

          <Group position="right" mt="xl">
            <Button
              variant="subtle"
              onClick={() => setOpened(false)}
              color="gray"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              loading={loading}
              disabled={
                !formData.slip ||
                !formData.bank_name ||
                !formData.reference_number
              }
            >
              Confirm Repayment
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

export default LoanRequestReportDetail;
